import { useEffect, useState, useCallback } from "react";
import { api, FUNCTIONS } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Check, X, Plus, ShieldCheck, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

export default function ApprovalsPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", function: "sales", project_id: "", notes: "", amount: 0 });
  const [expanded, setExpanded] = useState(null);
  const [commentText, setCommentText] = useState("");

  const load = useCallback(() => api.get("/approvals").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, [load]);

  const decide = async (id, status) => {
    await api.patch(`/approvals/${id}`, { status });
    toast.success(`Approval ${status}`); load();
  };

  const submit = async () => {
    if (!form.title.trim() || !form.function) { toast.error("Title and function required"); return; }
    await api.post("/approvals", { ...form, type: form.function });
    setOpen(false); load(); toast.success("Approval requested");
    setForm({ title: "", function: "sales", project_id: "", notes: "", amount: 0 });
  };

  const addComment = async (aid) => {
    if (!commentText.trim()) return;
    await api.post(`/approvals/${aid}/comments`, { text: commentText });
    setCommentText(""); load();
  };

  const pending = items.filter((a) => a.status === "pending");
  const decided = items.filter((a) => a.status !== "pending");

  return (
    <div className="space-y-4" data-testid="approvals-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Decisions</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Approvals Queue</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-approval-btn"><Plus className="h-4 w-4 mr-2" />New Request</Button>
      </div>

      <Card className="p-5 bg-card border-border">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-4 w-4 text-amber-400" />
          <h3 className="font-display font-semibold">Awaiting Decision</h3>
          <Badge variant="outline" className="ml-2">{pending.length}</Badge>
        </div>
        {pending.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border rounded">All caught up.</div>
        ) : (
          <div className="space-y-2">
            {pending.map((a) => (
              <div key={a.id} className="rounded border border-border bg-secondary/30" data-testid={`approval-row-${a.id}`}>
                <div className="flex items-center gap-4 p-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium">{a.title}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {(a.function || a.type || "").replace(/_/g, " ")}
                      {a.project_id && projects.find((p) => p.id === a.project_id) && <> · {projects.find((p) => p.id === a.project_id).client_name}</>}
                    </div>
                    {a.notes && <div className="text-xs text-muted-foreground mt-1">📝 {a.notes}</div>}
                  </div>
                  <button
                    onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    data-testid={`approval-comments-toggle-${a.id}`}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    {(a.comments || []).length}
                  </button>
                  <Button size="sm" onClick={() => decide(a.id, "approved")} data-testid={`approve-${a.id}`}><Check className="h-4 w-4 mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(a.id, "rejected")} data-testid={`reject-${a.id}`}><X className="h-4 w-4 mr-1" />Reject</Button>
                </div>
                {expanded === a.id && (
                  <div className="border-t border-border p-3 bg-background/40 space-y-2">
                    {(a.comments || []).length === 0 && <div className="text-xs text-muted-foreground italic">No comments yet.</div>}
                    {(a.comments || []).map((c) => (
                      <div key={c.id} className="text-xs">
                        <span className="font-semibold">{c.user_name}</span>
                        <span className="text-muted-foreground/70 font-mono ml-2">{new Date(c.at).toLocaleString("en-IN")}</span>
                        <div className="mt-0.5">{c.text}</div>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <Input placeholder="Add a comment…" value={commentText} onChange={(e) => setCommentText(e.target.value)}
                             onKeyDown={(e) => e.key === "Enter" && addComment(a.id)} data-testid={`approval-comment-input-${a.id}`} />
                      <Button size="sm" onClick={() => addComment(a.id)} data-testid={`approval-comment-send-${a.id}`}><Send className="h-4 w-4" /></Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="bg-card border-border">
        <div className="p-5 border-b border-border"><h3 className="font-display font-semibold">History</h3></div>
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Function</TableHead><TableHead>Status</TableHead><TableHead>Decided</TableHead></TableRow></TableHeader>
          <TableBody>
            {decided.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.title}</TableCell>
                <TableCell className="capitalize">{(a.function || a.type || "").replace(/_/g, " ")}</TableCell>
                <TableCell>
                  <Badge className={a.status === "approved" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-red-500/20 text-red-300 border-red-500/30"}>
                    {a.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono">{a.decided_at ? new Date(a.decided_at).toLocaleDateString() : "—"}</TableCell>
              </TableRow>
            ))}
            {decided.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No history yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>New Approval Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <AField label="Title" required>
              <Input placeholder="e.g. Discount 4% on Malhotra Villa" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="approval-title-input" />
            </AField>
            <div className="grid grid-cols-2 gap-3">
              <AField label="Function" required>
                <Select value={form.function} onValueChange={(v) => setForm({ ...form, function: v })}>
                  <SelectTrigger data-testid="approval-function-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FUNCTIONS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
                </Select>
              </AField>
              <AField label="Project" hint="Link to a project (optional)">
                <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No project</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.client_name}</SelectItem>)}</SelectContent>
                </Select>
              </AField>
            </div>
            <AField label="Amount (₹)" hint="Optional — only for money-related approvals">
              <Input type="number" placeholder="0" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} />
            </AField>
            <AField label="Notes" hint="Context, justification, links">
              <Textarea placeholder="Why is this being requested?" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </AField>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="approval-save-btn">Submit</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AField({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}
